(function () {
  'use strict';

  var QUEUE_KEY = 'jl_lead_queue';
  var MAX_QUEUE_ENTRIES = 50;
  var QUEUE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  var WORKER_ENDPOINT = 'https://jainsons-leads.jainsons.workers.dev/lead';
  var ARIA_ENDPOINT = 'https://aria.jainsonslights.com/lead';

  function trimValue(value) {
    return String(value || '').replace(/^\s+|\s+$/g, '');
  }

  function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  function readQueue() {
    try {
      var raw = window.localStorage.getItem(QUEUE_KEY);
      var parsed;

      if (!raw) {
        return [];
      }

      parsed = JSON.parse(raw);
      return Object.prototype.toString.call(parsed) === '[object Array]' ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeQueue(queue) {
    try {
      window.localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      /* Private browsing or storage restrictions must not interrupt a lead. */
    }
  }

  function queueEntryKey(entry) {
    try {
      return String(entry.endpoint || '') + '|' + String(entry.firstAttemptAt || '') + '|' + JSON.stringify(entry.payload || null);
    } catch (error) {
      return null;
    }
  }

  function sameQueueEntry(first, second) {
    var firstKey = queueEntryKey(first);
    var secondKey = queueEntryKey(second);

    return firstKey !== null && firstKey === secondKey;
  }

  function addToQueue(endpoint, payload, contentType) {
    var queue = readQueue();

    try {
      while (queue.length >= MAX_QUEUE_ENTRIES) {
        queue.shift();
      }

      queue.push({
        endpoint: endpoint,
        payload: payload,
        contentType: contentType || 'application/json',
        firstAttemptAt: new Date().toISOString(),
        attempts: 3
      });
      writeQueue(queue);
    } catch (error) {
      /* A queue failure must never affect the WhatsApp handoff. */
    }
  }

  function removeFromQueue(entry) {
    var queue = readQueue();
    var index;

    try {
      for (index = 0; index < queue.length; index += 1) {
        if (sameQueueEntry(queue[index], entry)) {
          queue.splice(index, 1);
          break;
        }
      }
      writeQueue(queue);
    } catch (error) {
      /* Storage is best-effort only. */
    }
  }

  function incrementQueuedAttempts(entry) {
    var queue = readQueue();
    var index;
    var attempts = Number(entry.attempts) || 0;

    try {
      for (index = 0; index < queue.length; index += 1) {
        if (sameQueueEntry(queue[index], entry)) {
          queue[index].attempts = attempts + 1;
          break;
        }
      }
      writeQueue(queue);
    } catch (error) {
      /* Storage is best-effort only. */
    }
  }

  function isQueueEntryStale(entry) {
    var firstAttemptAt = Date.parse(entry.firstAttemptAt || '');

    return !isNaN(firstAttemptAt) && (new Date().getTime() - firstAttemptAt > QUEUE_MAX_AGE_MS);
  }

  function sendJson(endpoint, payload, onSuccess, onFailure, contentType) {
    var finished = false;

    function succeed() {
      if (finished) {
        return;
      }
      finished = true;
      try {
        onSuccess();
      } catch (error) {
        /* Callback errors must not leak to the storefront. */
      }
    }

    function fail() {
      if (finished) {
        return;
      }
      finished = true;
      try {
        onFailure();
      } catch (error) {
        /* Callback errors must not leak to the storefront. */
      }
    }

    try {
      if (typeof window.fetch !== 'function') {
        fail();
        return;
      }

      var request = window.fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': contentType || 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!request || typeof request.then !== 'function') {
        fail();
        return;
      }

      request.then(function (response) {
        if (response && response.ok) {
          succeed();
        } else {
          fail();
        }
      }, fail);
    } catch (error) {
      fail();
    }
  }

  function warnFinalDeliveryFailure() {
    try {
      if (window.console && typeof window.console.warn === 'function') {
        window.console.warn('[jl-lead-capture] Lead delivery failed after retries.');
      }
    } catch (error) {
      /* Console access can be restricted in embedded browsers. */
    }
  }

  function sendWithRetries(endpoint, payload, contentType) {
    function attempt(attemptNumber) {
      sendJson(endpoint, payload, function () {
        /* Delivered successfully. */
      }, function () {
        var retryDelay;

        if (attemptNumber < 3) {
          retryDelay = attemptNumber === 1 ? 600 : 1800;
          window.setTimeout(function () {
            attempt(attemptNumber + 1);
          }, retryDelay);
          return;
        }

        addToQueue(endpoint, payload, contentType);
        warnFinalDeliveryFailure();
      }, contentType);
    }

    try {
      attempt(1);
    } catch (error) {
      addToQueue(endpoint, payload, contentType);
      warnFinalDeliveryFailure();
    }
  }

  function isUsableQueueEntry(entry) {
    return entry && typeof entry.endpoint === 'string' && entry.endpoint && entry.payload;
  }

  function flushQueue() {
    var queue = readQueue();
    var index = 0;

    function continueFlush() {
      if (index < queue.length) {
        window.setTimeout(processNext, 400);
      }
    }

    function processNext() {
      var entry = queue[index];
      var attempts;

      index += 1;

      try {
        if (!isUsableQueueEntry(entry)) {
          removeFromQueue(entry);
          continueFlush();
          return;
        }

        attempts = Number(entry.attempts) || 0;
        if (attempts >= 8 || isQueueEntryStale(entry)) {
          removeFromQueue(entry);
          continueFlush();
          return;
        }

        sendJson(entry.endpoint, entry.payload, function () {
          removeFromQueue(entry);
          continueFlush();
        }, function () {
          if (attempts >= 8 || isQueueEntryStale(entry)) {
            removeFromQueue(entry);
          } else {
            incrementQueuedAttempts(entry);
          }
          continueFlush();
        }, entry.contentType);
      } catch (error) {
        continueFlush();
      }
    }

    if (queue.length) {
      processNext();
    }
  }

  function collectLabeledFields(form) {
    var fields = form.querySelectorAll('input[data-wa-label], select[data-wa-label]');
    var labeledFields = [];
    var index;
    var label;
    var value;

    for (index = 0; index < fields.length; index += 1) {
      label = fields[index].getAttribute('data-wa-label');
      value = trimValue(fields[index].value);

      if (label && value) {
        labeledFields.push({
          label: label,
          value: value
        });
      }
    }

    return labeledFields;
  }

  function collectFormFields(form) {
    var fields = form.querySelectorAll('input[name], select[name]');
    var payload = {};
    var index;
    var name;

    for (index = 0; index < fields.length; index += 1) {
      name = fields[index].getAttribute('name');
      if (name) {
        payload[name] = trimValue(fields[index].value);
      }
    }

    return payload;
  }

  function capitalized(value) {
    value = trimValue(value);
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }

  function initForm(form) {
    form.addEventListener('submit', function (event) {
      try {
        var mobileField;
        var mobile;
        var waNumber;
        var waMessage;
        var customerType;
        var sourceDetail;
        var labeledFields;
        var formFields;
        var message;
        var summaryParts = [];
        var projectParts = [];
        var analyticsData;
        var workerPayload;
        var ariaPayload;
        var button;
        var note;
        var originalButtonText;
        var index;

        event.preventDefault();

        mobileField = form.querySelector('[name="mobile"]');
        mobile = trimValue(mobileField ? mobileField.value : '');
        if (!mobileField || mobile.replace(/\D/g, '').length < 10) {
          if (mobileField && typeof mobileField.focus === 'function') {
            mobileField.focus();
          }
          return;
        }

        waNumber = trimValue(form.getAttribute('data-wa-number'));
        waMessage = form.getAttribute('data-wa-message') || '';
        customerType = trimValue(form.getAttribute('data-customer-type'));
        sourceDetail = trimValue(form.getAttribute('data-source-detail'));
        labeledFields = collectLabeledFields(form);
        formFields = collectFormFields(form);
        message = waMessage;

        for (index = 0; index < labeledFields.length; index += 1) {
          message += '\n' + labeledFields[index].label + ': ' + labeledFields[index].value;
          summaryParts.push(labeledFields[index].label + ': ' + labeledFields[index].value);
          projectParts.push(labeledFields[index].label + ': ' + labeledFields[index].value);
        }

        button = form.querySelector('button[type="submit"]');
        note = form.querySelector('[data-jl-lead-note]');
        originalButtonText = button ? button.textContent : '';

        try {
          window.open('https://wa.me/' + waNumber + '?text=' + encodeURIComponent(message), '_blank');
        } catch (error) {
          /* The visitor can still use the form if a browser blocks a new tab. */
        }

        if (button) {
          button.textContent = 'Opening WhatsApp…';
        }
        if (note) {
          note.textContent = 'Opening WhatsApp — we usually reply within the hour.';
        }
        window.setTimeout(function () {
          try {
            if (button) {
              button.textContent = originalButtonText;
            }
          } catch (error) {
            /* UI restoration is best-effort only. */
          }
        }, 2500);

        analyticsData = { src: sourceDetail };
        for (index = 0; index < labeledFields.length; index += 1) {
          analyticsData[labeledFields[index].label] = labeledFields[index].value;
        }
        try {
          if (window.jlTrack) {
            window.jlTrack('whatsapp_click', analyticsData);
          }
        } catch (error) {
          /* Analytics must never interrupt lead capture. */
        }

        if (!hasOwn(formFields, 'customer_type')) {
          formFields.customer_type = customerType;
        }
        if (!hasOwn(formFields, 'source_detail')) {
          formFields.source_detail = sourceDetail;
        }
        formFields.source = sourceDetail;

        workerPayload = formFields;
        workerPayload.phone = mobile;
        workerPayload.note = 'Lead via ' + sourceDetail + (summaryParts.length ? ' — ' + summaryParts.join(' / ') : '');

        ariaPayload = {
          mobile: mobile,
          project_type: capitalized(customerType) + (projectParts.length ? ' — ' + projectParts.join(' / ') : ''),
          source_campaign: sourceDetail,
          source_url: window.location.href
        };
        if (hasOwn(formFields, 'name')) {
          ariaPayload.name = formFields.name;
        }

        sendWithRetries(WORKER_ENDPOINT, workerPayload);
        sendWithRetries(ARIA_ENDPOINT, ariaPayload);
      } catch (error) {
        /* WhatsApp has already been opened before any fallible delivery work. */
      }
    });
  }

  function initForms() {
    var forms;
    var index;

    try {
      forms = document.querySelectorAll('form[data-jl-lead]');
      for (index = 0; index < forms.length; index += 1) {
        try {
          initForm(forms[index]);
        } catch (error) {
          /* One invalid form must not stop other lead forms from working. */
        }
      }
    } catch (error) {
      /* DOM discovery is best-effort only. */
    }
  }

  window.jlReliablePost = function (endpoint, payload, contentType) {
    try {
      sendWithRetries(endpoint, payload, contentType);
    } catch (error) {
      /* A caller must never see an exception from this helper. */
    }
  };

  try {
    flushQueue();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initForms);
    } else {
      initForms();
    }
  } catch (error) {
    /* This shared asset must never surface an uncaught storefront error. */
  }
}());
